import React, { useEffect, useState } from "react";
import { Award, Loader2, X, Sparkles, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useNavigate } from "react-router-dom";
import TR from "@/i18n_cert";

/**
 * Gold "Držiteľ certifikátu SauberEko®" banner shown on Pricing page.
 * Real SauberEko cert holders submit cert no + name → admin verifies → free PRO PERMANENTLY (paper cert is for life).
 *
 * States:
 *  - logged-out: shows banner + redirects to register on click
 *  - logged-in + no request: shows banner with "Aktivovať zdarma" button → opens modal
 *  - pending: shows "Čaká na overenie" pill
 *  - approved + already_certified: shows green "Aktívne — PRO ZDARMO" badge
 *  - rejected: shows red banner with reason + chance to re-submit
 */
export default function CertHolderBanner() {
  const { user } = useAuth();
  const { lang } = useLang() || { lang: "sk" };
  const t = TR[lang] || TR.sk;
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/cert/status").then((r) => setStatus(r.data)).catch(() => {});
  }, [user]);

  const refreshStatus = () => {
    if (!user) return;
    api.get("/cert/status").then((r) => setStatus(r.data)).catch(() => {});
  };

  // Render bottom-right state pill on the banner
  const renderStateBadge = () => {
    if (!user) return null;
    if (!status) return null;
    if (status.already_certified) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500/10 border border-emerald-600 text-emerald-300 text-[11px] font-mono uppercase tracking-wider">
          <CheckCircle2 className="w-3 h-3" /> {t.state_active} · {status.certificate_no}
        </span>
      );
    }
    if (status.request_status === "pending") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/10 border border-amber-600 text-amber-300 text-[11px] font-mono uppercase tracking-wider">
          <Loader2 className="w-3 h-3 animate-spin" /> {t.state_pending}
        </span>
      );
    }
    if (status.request_status === "rejected") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-500/10 border border-red-700 text-red-300 text-[11px] font-mono uppercase tracking-wider">
          <AlertCircle className="w-3 h-3" /> {t.state_rejected}
        </span>
      );
    }
    return null;
  };

  return (
    <>
      <div
        data-testid="cert-holder-banner"
        className="relative overflow-hidden border border-gold-600 bg-gradient-to-br from-gold-500/15 via-gold-500/8 to-gold-500/15 p-6 md:p-7 mb-10"
        style={{
          boxShadow: "0 12px 60px -20px rgba(212,175,55,0.45)",
        }}
      >
        {/* subtle shine */}
        <div className="absolute inset-y-0 left-1/3 w-1/2 pointer-events-none" style={{
          background: "linear-gradient(90deg, transparent 0%, rgba(212,175,55,0.10) 50%, transparent 100%)",
        }} />

        <div className="relative flex flex-col md:flex-row md:items-center gap-5">
          <div className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-gold-400 to-gold-600 border-2 border-gold-300 flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.6)]" style={{ borderRadius: "50%" }}>
            <Award className="w-8 h-8 md:w-10 md:h-10 text-black" strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="wb-tag !text-gold-300 !border-gold-500 inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> {t.elite_zdarma}
              </span>
              <span className="text-[10px] font-mono uppercase tracking-wider text-gold-400/80">
                {t.for_holders}
              </span>
              {renderStateBadge()}
            </div>
            <h3 className="font-display font-black text-xl md:text-2xl text-white leading-tight">
              {t.headline_q} <span className="text-gold-400">{t.headline_company}</span>{t.headline_q_end}
              <br className="hidden md:block" />
              <span className="text-gold-400">{t.headline_free}</span>
            </h3>
            <p className="text-xs md:text-sm text-zinc-300 mt-2 leading-relaxed max-w-2xl">
              {t.desc} <strong className="text-gold-300">{t.desc_fields}</strong> {t.desc_mid} <strong className="text-gold-300">{t.desc_full_pro}</strong>{t.desc_end} <strong className="text-gold-300">{t.desc_ai}</strong> {t.desc_badge}
            </p>
          </div>
          <div className="flex-shrink-0">
            {!user ? (
              <button
                onClick={() => nav("/register?next=/pricing&cert=1")}
                data-testid="cert-banner-register-cta"
                className="wb-btn-primary px-5 py-3 text-sm font-bold inline-flex items-center gap-2"
              >
                <Award className="w-4 h-4" /> {t.cta_activate}
              </button>
            ) : status?.already_certified ? (
              <a
                href="/profile"
                className="wb-btn-ghost px-5 py-3 text-sm inline-flex items-center gap-2"
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-400" /> {t.cta_see_badge}
              </a>
            ) : status?.request_status === "pending" ? (
              <button
                disabled
                className="px-5 py-3 text-sm border border-amber-600 text-amber-300 bg-amber-500/10 cursor-not-allowed inline-flex items-center gap-2"
              >
                <Loader2 className="w-4 h-4 animate-spin" /> {t.cta_pending}
              </button>
            ) : (
              <button
                onClick={() => setOpen(true)}
                data-testid="cert-banner-open-modal"
                className="wb-btn-primary px-5 py-3 text-sm font-bold inline-flex items-center gap-2"
              >
                <Award className="w-4 h-4" /> {status?.request_status === "rejected" ? t.cta_resubmit : t.cta_activate}
              </button>
            )}
          </div>
        </div>

        {status?.request_status === "rejected" && status?.rejection_reason && (
          <div className="relative mt-4 border-l-2 border-red-600 bg-red-950/20 p-3 text-xs text-red-300">
            <strong>{t.rejection_label}</strong> {status.rejection_reason}
          </div>
        )}
      </div>

      {open && (
        <CertRequestModal
          t={t}
          onClose={() => setOpen(false)}
          onSubmitted={() => { setOpen(false); refreshStatus(); }}
        />
      )}
    </>
  );
}

function CertRequestModal({ t, onClose, onSubmitted }) {
  const [certNo, setCertNo] = useState("");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [note, setNote] = useState("");
  const [certRole, setCertRole] = useState("professional"); // "professional" | "recruiter"
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!certNo.trim() || !first.trim() || !last.trim()) {
      setError(t.error_required);
      return;
    }
    setBusy(true);
    try {
      await api.post("/cert/request", {
        certificate_no: certNo.trim(),
        first_name: first.trim(),
        last_name: last.trim(),
        note: note.trim() || null,
        cert_role: certRole,
      });
      setDone(true);
      setTimeout(() => onSubmitted?.(), 1500);
    } catch (e) {
      setError(e.response?.data?.detail || t.error_send);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        data-testid="cert-modal"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg bg-[var(--wb-bg)] border border-gold-700 shadow-2xl"
        style={{ boxShadow: "0 30px 90px -10px rgba(212,175,55,0.5)" }}
      >
        <button onClick={onClose} aria-label={t.close} className="absolute top-3 right-3 text-zinc-500 hover:text-white p-1.5" data-testid="cert-modal-close">
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 border-b border-gold-800/50 bg-gradient-to-br from-gold-500/10 to-transparent">
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-gold-400" />
            <span className="wb-tag !text-gold-300 !border-gold-500">{t.modal_tag}</span>
          </div>
          <h2 className="font-display font-black text-2xl mt-2">{t.modal_title}</h2>
          <p className="text-sm text-zinc-400 mt-1">
            {t.modal_sub}
          </p>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="cert-modal-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <h3 className="font-display font-black text-xl">{t.success_title}</h3>
            <p className="text-sm text-zinc-400 mt-2">
              {t.success_body}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-zinc-400 block mb-1.5">
                {t.label_cert_no}
              </label>
              <input
                data-testid="cert-input-no"
                type="text"
                value={certNo}
                onChange={(e) => setCertNo(e.target.value)}
                placeholder={t.placeholder_cert_no}
                className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2.5 text-sm font-mono focus:border-gold-500 outline-none"
                required
                autoFocus
              />
              <div className="text-[11px] text-zinc-600 mt-1">{t.hint_cert_no}</div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-zinc-400 block mb-1.5">
                  {t.label_first}
                </label>
                <input
                  data-testid="cert-input-first"
                  type="text"
                  value={first}
                  onChange={(e) => setFirst(e.target.value)}
                  placeholder={t.placeholder_first}
                  className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2.5 text-sm focus:border-gold-500 outline-none"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-zinc-400 block mb-1.5">
                  {t.label_last}
                </label>
                <input
                  data-testid="cert-input-last"
                  type="text"
                  value={last}
                  onChange={(e) => setLast(e.target.value)}
                  placeholder={t.placeholder_last}
                  className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2.5 text-sm focus:border-gold-500 outline-none"
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-zinc-400 block mb-1.5">
                {t.label_cert_role}
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="cert-role-professional"
                  onClick={() => setCertRole("professional")}
                  className={`p-3 border text-left transition-colors ${
                    certRole === "professional"
                      ? "border-gold-500 bg-gold-950/30"
                      : "border-zinc-700 bg-zinc-900 hover:border-zinc-500"
                  }`}
                >
                  <div className={`text-sm font-semibold ${certRole === "professional" ? "text-gold-300" : "text-zinc-200"}`}>
                    {t.role_professional}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">
                    {t.role_professional_desc}
                  </div>
                </button>
                <button
                  type="button"
                  data-testid="cert-role-recruiter"
                  onClick={() => setCertRole("recruiter")}
                  className={`p-3 border text-left transition-colors ${
                    certRole === "recruiter"
                      ? "border-gold-500 bg-gold-950/30"
                      : "border-zinc-700 bg-zinc-900 hover:border-zinc-500"
                  }`}
                >
                  <div className={`text-sm font-semibold ${certRole === "recruiter" ? "text-gold-300" : "text-zinc-200"}`}>
                    {t.role_recruiter}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">
                    {t.role_recruiter_desc}
                  </div>
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-zinc-400 block mb-1.5">
                {t.label_note}
              </label>
              <textarea
                data-testid="cert-input-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t.placeholder_note}
                rows={2}
                maxLength={500}
                className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2.5 text-sm focus:border-gold-500 outline-none resize-none"
              />
            </div>

            {error && (
              <div className="border-l-2 border-red-600 bg-red-950/20 p-3 text-xs text-red-300">
                {error}
              </div>
            )}

            <div className="flex items-center gap-2 pt-2">
              <button
                type="submit"
                disabled={busy}
                data-testid="cert-submit-btn"
                className="wb-btn-primary px-5 py-2.5 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
                {t.btn_submit}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="wb-btn-ghost px-4 py-2.5 text-sm"
              >
                {t.btn_cancel}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
